import { Container, DataGridTable } from 'components'
import { riskDashboard } from 'constants/tableData'
import styles from './riskDetails.module.scss'
import { useNavigate } from 'react-router'
import { useGridApiRef } from '@mui/x-data-grid'
import { useEffect, useState, useMemo } from 'react'
import FleetColumnHeader from './FleetColumnHeader'
import clientSearch from 'tools/searchQuery'
import strings from 'strings'
import { exportTableAsCSV } from 'tools/helper'

const t = strings.TABLE_DEFAULTS
const e = strings.CSV_EXPORT

interface CarrierCode {
    codeType: string;
    codeValue: string;
}

interface RiskDetailsRow {
    fleetName: string;
    eldProvider: string;
    companyEldAuthenticationId: string;
    carrierId?: string;
    policyId?: string;
    carrierCodes: CarrierCode[];
}

interface IRiskDetailsProps {
    print: boolean,
    isLoading: boolean,
    detailsData: RiskDetailsRow[],
    isAgent: boolean
}

const RiskDetails: React.FC<IRiskDetailsProps> = (props) => {
    const { print, isLoading, detailsData, isAgent } = props
    const { riskColumns, riskGroups } = riskDashboard
    const navigate = useNavigate()
    const apiRef = useGridApiRef()

    const [riskColumnsState, setRiskColumnsState] = useState<any>(riskColumns)
    const [riskGroupsState, setRiskGroupsState] = useState<any>(riskGroups)
    const [loadingRiskTable, setLoadingRiskTable] = useState<boolean>(true)
    const [visibleCodeTypes, setVisibleCodeTypes] = useState<string[]>([])
    const [availableCodeTypes, setAvailableCodeTypes] = useState<string[]>([])
    const [searchText, setSearchText] = useState<string>('')

    useEffect(() => {
        if (isAgent) {
            setRiskColumnsState(riskColumns.slice(0, 13))
            setRiskGroupsState(riskGroups.slice(0, 6))
        }

        const updatedColumns = riskColumns.map(column => {
            if (column.field === 'fleetName') {
                return {
                    ...column,
                    renderHeader: () => (
                        <FleetColumnHeader
                            availableCodeTypes={availableCodeTypes}
                            selectedTypes={visibleCodeTypes}
                            onFilterChange={handleCodeTypeFilterChange}
                        />
                    )
                }
            }
            return column
        })
        setRiskColumnsState(updatedColumns)
        setLoadingRiskTable(false)
    }, [availableCodeTypes, visibleCodeTypes])

    useEffect(() => {
        const types = new Set<string>()
        detailsData?.forEach((row: RiskDetailsRow) => {
            row?.carrierCodes?.forEach((code: CarrierCode) => {
                if (code.codeType) {
                    types.add(code.codeType)
                }
            })
            if (row?.policyId) { 
                types.add('POLICY_ID')
            }
        })
        const uniqueTypes = Array.from(types)
        setAvailableCodeTypes(uniqueTypes)
        setVisibleCodeTypes(uniqueTypes)
    }, [detailsData])

    const handleCodeTypeFilterChange = (selectedTypes: string[]) => {
        setVisibleCodeTypes(selectedTypes)
    }

    const handleQuickFilterChange = (value: string) => {
        setSearchText(value)
    }

    riskColumns[0].handleClick = (row: RiskDetailsRow) => {
        const originalRow = detailsData?.find((item: RiskDetailsRow) => 
            item.eldProvider === row.eldProvider && 
            item.companyEldAuthenticationId === row.companyEldAuthenticationId
        )
        
        if (!originalRow) {
            return
        }

        const dot = originalRow.carrierCodes.find((item: CarrierCode) => item.codeType === 'DOT' && item.codeValue !== t.DASH_VALUE)
        const mc = originalRow.carrierCodes.find((item: CarrierCode) => item.codeType === 'MC' && item.codeValue !== t.DASH_VALUE)
        const uuid = originalRow.carrierCodes.find((item: CarrierCode) => item.codeType === 'UUID' && item.codeValue !== t.DASH_VALUE)
        
        if (dot) {
            localStorage.setItem('carrierCodeType', 'DOT')
            localStorage.setItem('carrierCodeValue', dot.codeValue)
        }
        else if (mc) {
            localStorage.setItem('carrierCodeType', 'MC')
            localStorage.setItem('carrierCodeValue', mc.codeValue)
        }
        else if (uuid) {
            localStorage.setItem('carrierCodeType', 'UUID')
            localStorage.setItem('carrierCodeValue', uuid.codeValue)
        }
        else {
            localStorage.removeItem('carrierCodeType')
            localStorage.removeItem('carrierCodeValue')
        }
        
        localStorage.setItem('carrierPolicyId', originalRow.policyId || '')
        localStorage.setItem('carrierName', originalRow.fleetName)
        localStorage.setItem('eldProviderRisk', originalRow.eldProvider)
        originalRow.carrierId ? localStorage.setItem('carrierId', originalRow.carrierId) : localStorage.removeItem('carrierId')
        navigate('../carriers/details')
    }

    const filteredData = useMemo(() => {
        let filtered = searchText ? clientSearch(searchText, detailsData) : detailsData

        return filtered?.map((row: RiskDetailsRow) => {
            const filteredCodes = row.carrierCodes?.filter((code: CarrierCode) => 
                visibleCodeTypes.includes(code.codeType)
            ) || []

            return {
                ...row,
                carrierCodes: filteredCodes,
                policyId: visibleCodeTypes.includes('POLICY_ID') ? row.policyId : undefined
            }
        })
    }, [detailsData, searchText, visibleCodeTypes])

    const handleRiskExport = () => {
      const csvString = apiRef.current.getDataAsCsv()
      const csvRows = csvString.split('\n')
      const modifiedCsv = csvRows.slice(1).join('\n')
      exportTableAsCSV(modifiedCsv, e.RISK_CSV_EXPORT_NAME)
    }

    const tableMenuItems = [
      {
        label: e.DOWNLOAD_AS_CSV,
        key: 'export',
        action: () => {
          handleRiskExport()
        },
      },
    ]

    return (
        <>
            {!loadingRiskTable && (
                <Container
                    className={`${styles.riskDetailsContainer} ${
                        print ? styles.printContainer : ''
                    }`}
                >
                    <DataGridTable
                        data={filteredData}
                        columns={riskColumnsState}
                        columnGroupingModel={riskGroupsState}
                        columnHeaderHeight={30}
                        getRowId={(row: any) => row?.fleetName + '|' + row?.eldProvider + '|' + row?.companyEldAuthenticationId}
                        reduceHeight={280}
                        rowHeight={65}
                        showTitle={false}
                        showFilter={true}
                        multiSearchFilter={false}
                        checkboxSelection={false}
                        loading={isLoading}
                        autoHeight={print}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        sortField={'totalMiles'}
                        sortOrder={'desc'}
                        menuList={tableMenuItems}
                        apiRef={apiRef}
                        onSingleQuickFilterChange={handleQuickFilterChange}
                    />
                </Container>
            )}
        </>
    )
}

export default RiskDetails